import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import Link from "next/link";
import { useMemo } from "react";
import { API_URL } from "../../../.env.current";
import { useBackend, useQueryKeyAndFn } from "../../util/backend";
import { base64Encode } from "../../util/string";
import ImageWithFallback from "./ImageWithFallback";

export type UserAvatarProps = {
  userId?: string;
  username?: string;
  email?: string;
  timestamp?: Date;
  className?: string;
  showPlaceholder?: boolean;
  linkToProfile?: boolean;
};

export default function UserAvatar({
  userId: _userId,
  username: _username,
  email: _email,
  timestamp,
  className,
  showPlaceholder = true,
  linkToProfile = false,
}: UserAvatarProps) {
  const [userId, username, email] = useMemo(() => {
    if (_email?.endsWith("@users.gitlip.com")) {
      const userId = _email.split("@")[0];
      return [userId, _username, undefined];
    }
    return [_userId, _username, _email];
  }, [_userId, _username, _email]);

  const avatarSrc = useMemo(() => {
    let ids = 0;
    if (userId) ids += 1;
    if (username) ids += 1;
    if (email) ids += 1;

    if (ids !== 1) return undefined;

    let url;
    if (userId) {
      url = `${API_URL}/v1/user/by-id/${userId}/avatar`;
    }
    if (username) {
      url = `${API_URL}/v1/user/by-username/${username}/avatar`;
    }
    if (email) {
      const encodedEmail = base64Encode(email);
      if (encodedEmail) {
        url = `${API_URL}/v1/user/by-email/${encodedEmail}/avatar`;
      }
    }

    if (!url) return undefined;

    if (timestamp) {
      url += `?${new URLSearchParams({ t: timestamp.getTime().toString() })}`;
    }

    return url;
  }, [userId, username, email, timestamp]);

  /* --- USERNAME BY ID ----------------------------------- */
  const { backendGetUsernameByUserId } = useBackend();
  const { key: keyUsernameByUserId, fn: queryUsernameByUserId } = useQueryKeyAndFn(
    "backendGetUsernameByUserId",
    backendGetUsernameByUserId,
    [userId!]
  );

  const { data: dataUsernameByUserId } = useQuery({
    queryKey: keyUsernameByUserId,
    queryFn: queryUsernameByUserId,
    retry: false,
    enabled: linkToProfile && !username && Boolean(userId),
  });

  /* --- USERNAME BY EMAIL -------------------------------- */
  const { backendGetUsernameByEmail } = useBackend();
  const { key: keyUsernameByEmail, fn: queryUsernameByEmail } = useQueryKeyAndFn(
    "backendGetUsernameByEmail",
    backendGetUsernameByEmail,
    [email!]
  );

  const { data: dataUsernameByEmail } = useQuery({
    queryKey: keyUsernameByEmail,
    queryFn: queryUsernameByEmail,
    retry: false,
    enabled: linkToProfile && !username && !userId && Boolean(email),
  });

  /* --- SHOW IMAGE --------------------------------------- */
  const imageJsx = (
    <ImageWithFallback
      className={classNames(
        "overflow-hidden p-0.5 rounded-full flex-shrink-0",
        showPlaceholder &&
          "bg-origin-content bg-no-repeat bg-[url('/icons/missing/avatar.svg')] bg-[length:80%_80%] bg-[position:center]",
        className
      )}
      src={avatarSrc}
      fallback={"/images/blank.gif"}
      alt="User avatar"
    />
  );

  const usernameResolved = username ?? dataUsernameByUserId ?? dataUsernameByEmail;
  if (linkToProfile && usernameResolved) {
    return <Link href={`/@${usernameResolved}`}>{imageJsx}</Link>;
  }

  return imageJsx;
}
